import type { Dayjs } from 'dayjs';
import type {
  MagazineArticleInput as EntradaDoArtigo,
  MagazineIssueAdminPage as PaginaDeEdicoes,
  MagazineSection as SecaoDoArtigo,
} from '@titularis/api-client';

export type ValoresDoArtigo = Omit<EntradaDoArtigo, 'publishedAt' | 'references'> & {
  publishedAt?: Dayjs | null;
  references?: string;
};

export const SECAO_LABEL: Record<SecaoDoArtigo, string> = {
  article: 'Artigo',
  review: 'Resenha',
  'case-note': 'Comentário de julgado',
};

export type SituacaoDaEdicao = 'rascunho' | 'agendada' | 'no_ar';
export type SituacaoDoArtigo = SituacaoDaEdicao | 'aguarda_edicao';

export const SITUACAO_LABEL: Record<SituacaoDoArtigo, string> = {
  rascunho: 'Rascunho',
  agendada: 'Agendada',
  no_ar: 'No ar',
  aguarda_edicao: 'Aguarda edição',
};

export const SITUACAO_COR: Record<SituacaoDoArtigo, string> = {
  rascunho: 'default',
  agendada: 'processing',
  no_ar: 'success',
  aguarda_edicao: 'default',
};

export function entradaDoArtigo(valores: ValoresDoArtigo): EntradaDoArtigo {
  return {
    ...valores,
    subtitle: valores.subtitle?.trim() || null,
    publishedAt: valores.publishedAt?.toISOString() ?? null,
    keywords: valores.keywords ?? [],
    authors: valores.authors.map((autor) => ({
      name: autor.name,
      affiliation: autor.affiliation ?? '',
      bio: autor.bio ?? '',
    })),
    // Uma área só permite colar a bibliografia inteira do documento. Linhas
    // vazias separam visualmente as referências, mas não viram itens na API.
    references: (valores.references ?? '')
      .split(/\r?\n/)
      .map((linha) => linha.trim())
      .filter(Boolean),
  };
}

export function situacaoDaEdicao(
  publishedAt: string | null,
  agora = Date.now(),
): SituacaoDaEdicao {
  if (!publishedAt) return 'rascunho';
  return new Date(publishedAt).getTime() > agora ? 'agendada' : 'no_ar';
}

export function situacaoDoArtigo(
  publishedAt: string | null,
  publicacaoDaEdicao: string | null,
  agora = Date.now(),
): SituacaoDoArtigo {
  const situacao = situacaoDaEdicao(publishedAt, agora);
  if (situacao !== 'no_ar') return situacao;
  return situacaoDaEdicao(publicacaoDaEdicao, agora) === 'no_ar' ? 'no_ar' : 'aguarda_edicao';
}

/**
 * Só as edições continuam vindo inteiras: são poucas por natureza, e o seletor
 * do artigo precisa alcançar até a mais antiga. Aplicar a página da tabela ao
 * seletor faria uma edição existente desaparecer da escolha de quem cadastra.
 * Artigos têm paginação própria no servidor e não passam por esta função.
 */
export async function todasAsEdicoes(
  carregar: (pagina: number, tamanho: number) => Promise<PaginaDeEdicoes>,
): Promise<PaginaDeEdicoes['items']> {
  const tamanho = 60;
  const primeira = await carregar(1, tamanho);
  const edicoes = [...primeira.items];
  for (let pagina = 2; pagina <= Math.ceil(primeira.total / tamanho); pagina += 1) {
    const proxima = await carregar(pagina, tamanho);
    edicoes.push(...proxima.items);
  }
  return edicoes;
}
